import { forwardRef, lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { EntryList } from "../EntryList";
import { Bread } from "../Nav";
import { TitleBarScrollObsOuter } from "../TitleBar";

const NavDialog = lazy(() => import("../Nav/NavDialog"));

export interface ListLayoutProps {
  children?: ReactNode;
  listNode?: ReactNode;
  introNode?: ReactNode;
  activeSlug?: string;
  section?: { title: string; path: string };
  data?: { title: string; slug: string }[];
  inView?: boolean;
}

export const ListLayout = forwardRef<HTMLDivElement, ListLayoutProps>(
  (
    { children = null, listNode = null, introNode = null, activeSlug, section, data = [], inView },
    ref
  ) => {
    const titleProp = { title: section?.title ? section.title : "" };

    return (
      <>
        <div
          data-site-layout-part="list"
          className={`border-r border-e-color-outlineVariant ${
            activeSlug ? "hidden lg:block" : ""
          }`}
          ref={ref}
        >
          <TitleBarScrollObsOuter
            leadingNode={
              <div className={`w-10 h-10 lg:hidden -ml-2`}>
                <Suspense fallback={<>[menu]</>}>{inView ? <NavDialog /> : `[unem]`}</Suspense>
              </div>
            }
            trailingNode={
              <div className={`w-10 h-10 -mr-2`}>
                <button className={`w-10 h-10 items-center justify-center flex`}>
                  <svg width="24" height="24">
                    <use xlinkHref={`/shared-assets/images/icons.svg#search`} />
                  </svg>
                </button>
              </div>
            }
            initialContentNode={
              <>
                <ListTitle {...titleProp} displayCss={`hidden lg:block`} />
                <Bread path="notes" outerDisplayCss={`lg:hidden`} />
              </>
            }
            scrolledContentNode={<ListTitle {...titleProp} />}
          />

          <div className={`px-5 py-4 text-2xl lg:hidden`}>
            <ListTitle {...titleProp} />
          </div>

          {introNode && <div className={`lg:hidden`}>{introNode}</div>}

          {data?.length ? (
            <EntryList data={data} activeSlug={activeSlug} basePath={section?.path || ""} />
          ) : (
            listNode
          )}
        </div>
        <div data-site-layout-part="detail">
          {introNode && <div className={`hidden lg:block`}>{introNode}</div>}
          {children}
        </div>
      </>
    );
  }
);

ListLayout.displayName = "ListLayout";

// -----

interface ListTitleProps {
  title?: string;
  displayCss?: string;
}

const ListTitle = ({ title, displayCss = "" }: ListTitleProps) => {
  return title ? <p className={`font-bold ${displayCss}`}>{title}</p> : null;
};
